import {
  useState,
  useMemo,
  useCallback,
  useContext,
  createContext,
  useEffect,
} from 'react';
import {useServerProps, useUrl} from '@shopify/hydrogen';
import {useGlobalContainer} from './GlobalContainerProvider.client';

type SearchContextStateType = {
  isSearchOpen: boolean;
  includeOutOfStock: boolean;
  inTwlShop: boolean;
};

type SearchContextActionType = {
  openSearch: () => void;
  closeSearch: () => void;
  toggleSearch: () => void;
  setIncludeOutOfStock: (value: boolean) => void;
  setInTwlShop: (value: boolean) => void;
};

type SearchContextType = SearchContextStateType & SearchContextActionType;

const SearchContext = createContext<SearchContextType>(null);

function SearchUIProvider({children}) {
  const [open, setOpen] = useState<boolean>(false);

  const {setServerProps} = useServerProps();
  const [includeOutOfStock, setIncludeOutOfStock] = useState<boolean>(false);

  const {searchParams} = useUrl();
  const resolvedInTwlShop = useMemo(() => {
    const inTwlShopSearchParam = searchParams.get('inTwlShop');
    return inTwlShopSearchParam === null || inTwlShopSearchParam === 'true';
  }, [searchParams]);
  const [inTwlShop, setInTwlShop] = useState<boolean>(resolvedInTwlShop);

  useEffect(() => {
    setServerProps('includeOutOfStock', includeOutOfStock);
  }, [includeOutOfStock, setServerProps]);

  useEffect(() => {
    setServerProps('inTwlShop', inTwlShop);
  }, [inTwlShop, setServerProps]);

  const openSearch = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const closeSearch = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const toggleSearch = useCallback(() => {
    setOpen(!open);
  }, [setOpen, open]);

  const {setIsLivesearchActive} = useGlobalContainer();

  useEffect(() => {
    setIsLivesearchActive(open);
  }, [open, setIsLivesearchActive]);

  const contextValue = useMemo(() => {
    return {
      isSearchOpen: open,
      openSearch,
      closeSearch,
      toggleSearch,
      includeOutOfStock,
      setIncludeOutOfStock,
      inTwlShop,
      setInTwlShop,
    };
  }, [
    open,
    openSearch,
    closeSearch,
    toggleSearch,
    includeOutOfStock,
    setIncludeOutOfStock,
    inTwlShop,
    setInTwlShop,
  ]);

  return (
    <SearchContext.Provider value={contextValue}>
      {children}
    </SearchContext.Provider>
  );
}

function useSearchUI() {
  const search = useContext(SearchContext);
  if (search === undefined) {
    throw new Error('search context is undefined');
  }

  return search;
}

export {SearchUIProvider, useSearchUI};
